import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { ChevronUp, ArrowUp } from 'lucide-react';

/**
 * Premium ScrollToTop Component
 * Luxury-grade scroll-to-top functionality with sophisticated UX patterns and visual effects
 * Includes scroll progress, smart positioning, and premium animations
 */
const ScrollToTop = ({ 
  showAt = 400,
  hideAt = 100,
  position = 'bottom-right',
  variant = 'luxury',
  showProgress = true,
  smoothDuration = 1200,
  easing = 'luxury'
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  
  const buttonRef = useRef(null);
  const rafRef = useRef(null);

  // Luxury easing functions - optimized with useMemo
  const easingFunctions = useMemo(() => ({
    luxury: (t) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t,
    premium: (t) => t * t * (3 - 2 * t),
    elegant: (t) => 1 - Math.pow(1 - t, 3),
    sophisticated: (t) => t * t * t * (t * (6 * t - 15) + 10)
  }), []);

  // Enhanced scroll handler with performance optimization
  const handleScroll = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }

    rafRef.current = requestAnimationFrame(() => {
      const currentScrollY = window.scrollY;
      const documentHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercentage = Math.min((currentScrollY / documentHeight) * 100, 100);
      
      setScrollProgress(scrollPercentage);
      // Hide button when at bottom (within 50px of bottom) or when at top
      setIsVisible(currentScrollY > showAt && scrollPercentage < 95);
      setIsScrolling(Math.abs(currentScrollY - lastScrollY) > 5);
      setLastScrollY(currentScrollY);
    });
  }, [showAt, lastScrollY]);

  useEffect(() => {
    const throttledScroll = () => handleScroll();
    
    window.addEventListener('scroll', throttledScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', throttledScroll);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [handleScroll]);

  // Premium smooth scroll with custom easing
  const scrollToTop = useCallback(() => {
    const startY = window.scrollY;
    const startTime = performance.now();
    const easingFn = easingFunctions[easing] || easingFunctions.luxury;
    const fastDuration = 800; // Reduced from smoothDuration for faster response

    const animateScroll = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / fastDuration, 1);
      const easedProgress = easingFn(progress);
      
      const currentY = startY * (1 - easedProgress);
      window.scrollTo(0, currentY);

      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      }
    };

    // Start animation immediately
    requestAnimationFrame(animateScroll);
  }, [easing, easingFunctions]);

  // Position classes
  const positionClasses = {
    'bottom-right': 'bottom-8 right-8',
    'bottom-left': 'bottom-8 left-8',
    'bottom-center': 'bottom-8 left-1/2 transform -translate-x-1/2',
    'top-right': 'top-24 right-8',
    'top-left': 'top-24 left-8'
  };

  // Variant styles
  const variantStyles = {
    luxury: {
      button: [
        'bg-gray-900/95 hover:bg-gray-800',
        'text-amber-400 hover:text-amber-300',
        'shadow-2xl hover:shadow-amber-500/25',
        'backdrop-blur-sm border border-amber-400/30'
      ].join(' '),
      size: 'w-16 h-16',
      icon: 'h-5 w-5'
    },
    premium: {
      button: [
        'bg-white/90 hover:bg-white',
        'text-gray-700 hover:text-amber-500',
        'shadow-2xl border border-gray-200/50',
        'backdrop-blur-xl'
      ].join(' '),
      size: 'w-14 h-14',
      icon: 'h-5 w-5'
    },
    minimal: {
      button: [
        'bg-gray-900/80 hover:bg-gray-900',
        'text-white hover:text-amber-400',
        'shadow-lg backdrop-blur-sm'
      ].join(' '),
      size: 'w-12 h-12',
      icon: 'h-4 w-4'
    }
  };

  const currentVariant = variantStyles[variant] || variantStyles.luxury;

  // Removed quick actions as requested

  if (!isVisible) return null;

  return (
    <div className={`fixed z-40 transition-all duration-500 ease-out ${positionClasses[position]} ${
      isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
    }`}>
      


      {/* Architectural Scroll Button */}
      <button
        ref={buttonRef}
        onClick={scrollToTop}
        className={`${currentVariant.size} ${currentVariant.button} transition-all duration-500 ease-out transform hover:scale-110 flex items-center justify-center group relative overflow-hidden`}
        style={{ borderRadius: '8px' }}
        aria-label="Scroll to top"
        title="Back to Top"
      >
        {/* Architectural Progress Indicator */}
        {showProgress && (
          <div className="absolute bottom-0 left-0 w-full h-1 bg-amber-400/20">
            <div 
              className="h-full bg-gradient-to-r from-amber-400 to-amber-500 transition-all duration-300"
              style={{ width: `${scrollProgress}%` }}
            />
          </div>
        )}

        {/* Icon with architectural styling */}
        <div className="relative z-10">
          {variant === 'luxury' ? (
            <div className={`${currentVariant.icon} transition-all duration-500 ${
              isScrolling ? 'animate-pulse' : 'group-hover:-translate-y-1'
            }`}>
              <ArrowUp className="w-full h-full stroke-2" />
            </div>
          ) : (
            <ChevronUp className={`${currentVariant.icon} transition-all duration-300 ${
              isScrolling ? 'animate-pulse' : 'group-hover:-translate-y-1'
            }`} />
          )}
        </div>

        {/* Luxury hover effect */}
        {variant === 'luxury' && (
          <div className="absolute inset-0 bg-gradient-to-t from-amber-500/20 to-transparent transform scale-0 group-hover:scale-100 transition-transform duration-500 ease-out opacity-0 group-hover:opacity-100" style={{ borderRadius: '8px' }}></div>
        )}

        {/* Architectural accent line */}
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-400/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

        {/* Ripple effect on click */}
        <div className="absolute inset-0 bg-amber-400 opacity-0 group-active:opacity-20 transform scale-0 group-active:scale-100 transition-all duration-200" style={{ borderRadius: '8px' }}></div>
      </button>

      {/* Architectural percentage indicator */}
      {variant === 'luxury' && showProgress && (
        <div className="absolute -bottom-10 left-1/2 transform -translate-x-1/2 bg-gray-900/95 text-amber-400 text-xs px-3 py-1 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-all duration-300 border border-amber-400/30" style={{ borderRadius: '4px', fontFamily: 'Inter, sans-serif' }}>
          {Math.round(scrollProgress)}%
        </div>
      )}


    </div>
  );
};

// Higher-order component for multiple scroll-to-top instances
export const ScrollController = ({ 
  children, 
  showScrollToTop = true, 
  showScrollToBottom = false,
  ...scrollToTopProps 
}) => {
  const [showScrollBottom, setShowScrollBottom] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY;
      const clientHeight = window.innerHeight;
      const scrollPercentage = (scrollTop / (scrollHeight - clientHeight)) * 100;
      
      setShowScrollBottom(scrollPercentage < 80); // Show scroll-to-bottom when not near bottom
    };

    if (showScrollToBottom) {
      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [showScrollToBottom]);

  const scrollToBottom = () => {
    window.scrollTo({
      top: document.documentElement.scrollHeight,
      behavior: 'smooth'
    });
  };

  return (
    <>
      {children}
      
      {/* Scroll to Top */}
      {showScrollToTop && <ScrollToTop {...scrollToTopProps} />}
      
      {/* Scroll to Bottom */}
      {showScrollToBottom && showScrollBottom && (
        <button
          onClick={scrollToBottom}
          className="fixed bottom-8 left-8 w-14 h-14 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-110 flex items-center justify-center z-40"
          aria-label="Scroll to bottom"
        >
          <ChevronUp className="h-5 w-5 transform rotate-180" />
        </button>
      )}
    </>
  );
};

// Preset configurations for different use cases
export const ScrollToTopPresets = {
  luxury: {
    variant: 'luxury',
    showProgress: true,
    smoothDuration: 1200,
    easing: 'luxury',
    position: 'bottom-right'
  },
  
  minimal: {
    variant: 'minimal',
    showProgress: false,
    smoothDuration: 800,
    easing: 'elegant',
    position: 'bottom-right'
  },
  
  premium: {
    variant: 'premium',
    showProgress: true,
    smoothDuration: 1000,
    easing: 'premium',
    position: 'bottom-right'
  }
};

export default ScrollToTop;